import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractApiErrorMessage } from '@/lib/api-client';

import { useCompletePlaceholderMutation } from '../api/use-complete-placeholder-mutation';
import {
  INITIAL_PLACEHOLDER_STATE,
  PlaceholderForm,
  buildCompletePlaceholderBody,
  canSubmitPlaceholder,
  type PlaceholderFormState,
} from './complete-placeholder-form';

export interface CompletePlaceholderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly invoiceId: string;
  readonly onCompleted?: (() => void) | undefined;
}

export function CompletePlaceholderDialog(props: CompletePlaceholderDialogProps) {
  const [state, setState] = useState<PlaceholderFormState>(INITIAL_PLACEHOLDER_STATE);
  const mutation = useCompletePlaceholderMutation();

  function submit() {
    mutation.mutate(
      { invoiceId: props.invoiceId, body: buildCompletePlaceholderBody(state) },
      {
        onSuccess: () => {
          toast.success('Placeholder completed.');
          props.onOpenChange(false);
          props.onCompleted?.();
        },
        onError: (err) => {
          toast.error(extractApiErrorMessage(err, 'Could not complete placeholder.'));
        },
      },
    );
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Complete placeholder</DialogTitle>
          <DialogDescription>
            Fill in the missing details. After completion the invoice becomes a normal Draft ready
            for issuance.
          </DialogDescription>
        </DialogHeader>
        <PlaceholderForm state={state} setState={setState} />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              props.onOpenChange(false);
            }}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmitPlaceholder(state) || mutation.isPending}>
            {mutation.isPending ? 'Completing…' : 'Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
